import { useState } from 'react';
import { saveRoom, deleteRoom, GetAdminVenueDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Building2, Users } from 'lucide-react';

type Room = GetAdminVenueDataOutputType['rooms'][0];

const DEFAULT_ROOMS = [
  'Restaurant', 'Royal Enclosure', 'Lounge Bar', 'Main Bar',
  'Outside Front', 'Outside Back', 'Outside Front Decking', 'Outside Back Decking', 'Barn',
];

function RoomForm({ room, onSave, onCancel }: { room: Partial<Room> | null; onSave: (r: Partial<Room>) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState<Partial<Room>>(room || { name: '', description: '', active: true, displayOrder: 0 });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof Room) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const setNum = (k: keyof Room) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value ? parseFloat(e.target.value) : null }));

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Room name required'); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
      <p className="font-semibold text-foreground text-sm">{form.id ? 'Edit Room' : 'Add Room'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Room Name *</label>
          <Input list="room-presets" value={form.name || ''} onChange={set('name')} placeholder="e.g. Restaurant" />
          <datalist id="room-presets">
            {DEFAULT_ROOMS.map(r => <option key={r} value={r} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Display Order</label>
          <Input type="number" value={form.displayOrder ?? ''} onChange={e => setForm(f => ({ ...f, displayOrder: parseInt(e.target.value) || 0 }))} placeholder="1, 2, 3..." />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Description</label>
        <Textarea value={form.description || ''} onChange={set('description')} rows={2} placeholder="Brief description of the space..." />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Width (m)</label>
          <Input type="number" step="0.1" value={form.widthMetres ?? ''} onChange={setNum('widthMetres')} placeholder="e.g. 8.5" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Length (m)</label>
          <Input type="number" step="0.1" value={form.lengthMetres ?? ''} onChange={setNum('lengthMetres')} placeholder="e.g. 12.0" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Height (m)</label>
          <Input type="number" step="0.1" value={form.heightMetres ?? ''} onChange={setNum('heightMetres')} placeholder="e.g. 3.2" />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Floor Plan Image URL</label>
        <Input value={form.floorPlanUrl || ''} onChange={set('floorPlanUrl')} placeholder="https://..." />
        <p className="text-xs text-muted-foreground mt-1">Paste a direct image URL for the floor plan.</p>
        {form.floorPlanUrl && (
          <img src={form.floorPlanUrl} alt="Floor plan preview" className="mt-2 max-h-40 object-contain rounded border border-border" />
        )}
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
        <Textarea value={form.notes || ''} onChange={set('notes')} rows={2} placeholder="Any additional notes about this space..." />
      </div>
      <div className="flex items-center gap-2">
        <Switch id={`room-active-${form.id || 'new'}`} checked={form.active ?? true} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
        <label htmlFor={`room-active-${form.id || 'new'}`} className="text-sm text-muted-foreground cursor-pointer">Active</label>
      </div>
      <div className="flex items-center gap-2">
        <Switch id={`room-visible-${form.id || 'new'}`} checked={(form as any).visibleToStaff ?? false} onCheckedChange={v => setForm(f => ({ ...f, visibleToStaff: v } as any))} />
        <label htmlFor={`room-visible-${form.id || 'new'}`} className="text-sm text-muted-foreground cursor-pointer">Visible on staff Floor Plans page</label>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save Room'}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function VenueRoomsTab({ rooms, onRefresh }: { rooms: Room[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<Partial<Room> | null>(null);
  const [showNew, setShowNew] = useState(false);

  const handleSave = async (form: Partial<Room>) => {
    try {
      await saveRoom({ id: form.id, name: form.name!, description: form.description, widthMetres: form.widthMetres, lengthMetres: form.lengthMetres, heightMetres: form.heightMetres, floorPlanUrl: form.floorPlanUrl, displayOrder: form.displayOrder, active: form.active, notes: form.notes, visibleToStaff: (form as any).visibleToStaff ?? false });
      toast.success(form.id ? 'Room updated' : 'Room added');
      setEditing(null);
      setShowNew(false);
      onRefresh();
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteRoom({ id }); toast.success('Room deleted'); onRefresh(); }
    catch (e: any) { toast.error(e.message); }
  };

  const seedDefaultRooms = async () => {
    const existingNames = new Set(rooms.map(r => r.name.toLowerCase()));
    const toCreate = DEFAULT_ROOMS.filter(r => !existingNames.has(r.toLowerCase()));
    for (let i = 0; i < toCreate.length; i++) {
      await saveRoom({ name: toCreate[i], active: true, displayOrder: rooms.length + i + 1 });
    }
    toast.success(`Added ${toCreate.length} default rooms`);
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">{rooms.length} rooms configured</p>
        <div className="flex gap-2">
          {rooms.length === 0 && (
            <Button variant="outline" size="sm" onClick={seedDefaultRooms}>Add Default Rooms</Button>
          )}
          <Button size="sm" onClick={() => setShowNew(true)} className="gap-1.5"><Plus className="w-3.5 h-3.5" />Add Room</Button>
        </div>
      </div>

      {showNew && !editing && (
        <RoomForm room={null} onSave={handleSave} onCancel={() => setShowNew(false)} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rooms.map(room => (
          <div key={room.id}>
            {editing?.id === room.id ? (
              <RoomForm room={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
            ) : (
              <div className={`border rounded-xl p-4 ${!room.active ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    <span className="font-medium text-foreground text-sm">{room.name}</span>
                    {!room.active && <span className="text-xs text-muted-foreground">(hidden)</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditing(room); setShowNew(false); }}><Pencil className="w-3.5 h-3.5" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{room.name}"?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(room.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {room.description && <p className="text-xs text-muted-foreground mb-2">{room.description}</p>}
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {room.widthMetres && room.lengthMetres && <span>{room.widthMetres}m × {room.lengthMetres}m</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{room.tableCount} tables</span>
                  {room.floorPlanUrl && <span className="text-primary font-medium">📐 Floor plan</span>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
